import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Icon } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';
import { ALERT_TYPE_BY_ID } from './types';

const formatTimeAgo = (iso, t) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '';
  const min = Math.floor(ms / 60_000);
  if (min < 1) return t('tcn.m7.justNow', 'just now');
  if (min < 60) return t('tcn.m7.minAgo', '{{n}}m ago', { n: min });
  const hr = Math.floor(min / 60);
  if (hr < 24) return t('tcn.m7.hrAgo', '{{n}}h ago', { n: hr });
  const days = Math.floor(hr / 24);
  return t('tcn.m7.daysAgo', '{{n}}d ago', { n: days });
};

/**
 * Module 7 — alert list row.
 *
 * Compact card with icon + type label + payload summary. Visual cue for unread
 * (left bar accent + bold) vs read.
 */
const AlertRow = ({ alert, onActivate }) => {
  const { t } = useTranslation();
  if (!alert) return null;
  const meta = ALERT_TYPE_BY_ID[alert.type] ?? ALERT_TYPE_BY_ID.other;
  const isUnread = !alert.read_at;
  const payload = alert.payload_json || {};
  const isUuid = (v) => typeof v === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
  const subtitle = payload.tiktok_username
    ? `@${payload.tiktok_username}`
    : payload.component
      ? t(`tcn.m5.bonus.${payload.component}.label`, payload.component)
      : payload.metric
        ? payload.metric
        : isUuid(payload.entity_id)
          ? t('tcn.m7.row.unknownCreator', 'Unknown creator')
          : payload.entity_id || '';

  return (
    <button
      type="button"
      onClick={() => onActivate?.(alert)}
      style={{ all: 'unset', display: 'block', width: '100%', cursor: 'pointer', boxSizing: 'border-box' }}
    >
      <Card
        padded
        style={{
          padding: 12,
          background: isUnread ? `${meta.color}0a` : TCN.surface1,
          border: `1px solid ${isUnread ? `${meta.color}33` : TCN.border}`,
          borderInlineStart: isUnread ? `3px solid ${meta.color}` : `3px solid transparent`,
          transition: `all ${TCN.durBase} ${TCN.ease}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div
            style={{
              width: 32, height: 32, borderRadius: 8,
              background: `${meta.color}22`, color: meta.color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon name={meta.icon} size={16} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                marginBottom: 2,
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  fontWeight: isUnread ? 700 : 600,
                  color: isUnread ? meta.color : TCN.textDim,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                {t(`tcn.m7.type.${meta.id}`, meta.label)}
              </span>
              <span style={{ fontSize: 11, color: TCN.textMute, whiteSpace: 'nowrap' }}>
                {formatTimeAgo(alert.created_at, t)}
              </span>
            </div>
            <div
              style={{
                fontSize: 14,
                fontWeight: isUnread ? 600 : 500,
                color: isUnread ? TCN.text : TCN.textDim,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {subtitle || t('tcn.m7.row.fallback', 'Open to see details')}
            </div>
            {payload.severity && (
              <span
                style={{
                  display: 'inline-block',
                  marginTop: 4,
                  fontSize: 10,
                  fontWeight: 700,
                  color: TCN.textMute,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                {String(payload.severity).replace(/_/g, ' ')}
              </span>
            )}
          </div>
        </div>
      </Card>
    </button>
  );
};

export default AlertRow;
